import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";

const ShoppingEntertainment = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Shopping Districts",
      icon: "ShoppingBag",
      content: "Nepal offers a vibrant shopping experience, from traditional handicraft markets to modern shopping centers.",
      places: [
        { name: "Thamel, Kathmandu", desc: "The tourist hub with countless shops selling pashminas, handicrafts, trekking gear, and souvenirs." },
        { name: "Asan Bazaar, Kathmandu", desc: "One of the oldest and most authentic local markets for spices, grains, and daily goods." },
        { name: "Jawalakhel, Patan", desc: "Famous for Tibetan carpets and handicrafts, with the Tibetan Refugee Camp nearby." },
        { name: "Phewa Lake Area, Pokhara", desc: "Lakeside shops offering handicrafts, gemstones, and bohemian wear with mountain views." },
        { name: "Durbar Square areas", desc: "Each Durbar Square has surrounding bazaars with antiques, statues, and traditional Newari items." }
      ]
    },
    {
      title: "What to Buy",
      icon: "Gift",
      content: "Nepal is known for unique handicrafts and products that make perfect souvenirs:",
      items: [
        "Pashmina shawls and scarves (genuine ones from Khukri wool)",
        "Himalayan singing bowls - used for meditation and sound healing",
        "Thanka paintings - traditional Buddhist religious paintings",
        "Khukuri knives - the traditional Gurkha blade",
        "Tibetan carpets and rugs - handwoven with traditional patterns",
        "Gemstones and jewelry (amethyst, turquoise, lapis lazuli)",
        "Tea - especially Himalayan and herbal teas",
        "Ayurvedic products and essential oils"
      ]
    },
    {
      title: "Entertainment & Nightlife",
      icon: "Music",
      content: "Nepal's entertainment scene ranges from cultural performances to modern nightlife:",
      items: [
        "Cultural dance performances: Traditional Nepali dances at venues in Kathmandu and Pokhara",
        "Live music: Thamel has numerous bars with live rock, folk, and fusion music",
        "Movie theaters: Modern cinemas in Kathmandu show Nepali and international films",
        "Casinos: Several casinos in Kathmandu's 5-star hotels",
        "Everest IMAX: In Kumari Cinema, showing Everest documentary films",
        "Bars and pubs: Vibrant nightlife in Thamel, Jhamsikhel, and Lakeside Pokhara",
        "Meditation centers: Join group meditation sessions at Buddhist centers"
      ]
    },
    {
      title: "Cultural Experiences",
      icon: "Landmark",
      content: "Immerse yourself in Nepal's rich cultural heritage through these unique experiences:",
      items: [
        "Attend a traditional aarti ceremony at Pashupatinath Temple at sunset",
        "Visit a local family for a home-cooked Nepali meal",
        "Participate in a meditation retreat at a Buddhist monastery",
        "Learn to cook Nepali dishes with a cooking class",
        "Watch a traditional dance performance",
        "Visit a pottery square in Bhaktapur and try your hand at the wheel",
        "Explore Tibetan Buddhist monasteries and witness chanting monks"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1600&h=600&fit=crop)" }}>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="section-heading text-white mb-4">Shopping & Entertainment</h1>
              <p className="text-lg text-white/80">Discover the best shopping districts, local crafts, nightlife, and cultural entertainment in Nepal.</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            {sections.map((section, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Icon name={section.icon} size={18} className="text-accent" />
                  </div>
                  <h2 className="font-headline text-xl text-gray-900" style={{ fontWeight: 300 }}>{section.title}</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">{section.content}</p>
                {section.places ? (
                  <div className="space-y-3">
                    {section.places.map((place, j) => (
                      <div key={j} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                        <Icon name="MapPin" size={14} className="text-accent mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-sm text-gray-800">{place.name}</h4>
                          <p className="text-xs text-gray-500">{place.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {section.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                        <Icon name="Star" size={12} className="text-amber-400 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-white/50">
          &copy; {new Date().getFullYear()} Sherpa Trekker. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default ShoppingEntertainment;
